import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    Button,
    RadioGroup,
    FormControlLabel,
    Radio,
    Chip,
    Snackbar,
    Alert,
    Paper,
    Divider,
    CircularProgress,
    FormControl,
} from "@mui/material";
import { Refresh } from "@mui/icons-material";
import "./DataManagement.css";

// 4 selectable sources. Picking one and updating sets BOTH the raw model
// (e.g. "ECMWF-IFS") and its value-added counterpart ("ECMWF-IFS-VA")
// together, in one action — there's no longer a separate raw/value-added
// pair of pickers, since the two are never meant to point at different
// underlying sources.
const MODEL_OPTIONS = [
    {
        value: "METOFFICE",
        label: "METOFFICE",
        fullName: "Met Office - Forecast System",
        color: "#2563eb",
    },
    {
        value: "ECMWF-IFS",
        label: "ECMWF-IFS",
        fullName: "European Centre for Medium-Range Weather Forecasts - Integrated Forecast System",
        color: "#0ea5e9",
    },
    {
        value: "GFS",
        label: "GFS",
        fullName: "Global Forecast System (NOAA)",
        color: "#16a34a",
    },
    {
        value: "ICON",
        label: "ICON",
        fullName: "Icosahedral Nonhydrostatic Model (DWD)",
        color: "#7c3aed",
    },
];

const vaNameFor = (base) => `${base}-VA`;

const DataManagement = () => {
    const [currentBaseModel, setCurrentBaseModel] = useState("");
    const [currentVaModel, setCurrentVaModel] = useState("");
    const [selectedModel, setSelectedModel] = useState("");

    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const backendUrl = (process.env.REACT_APP_BACKEND_API_URL_LOCAL || '').replace(/\/$/, '');
    const API_URL = `${backendUrl}/forecastmodels/set-source`;

    // Fetch both active sources (raw + value-added) so we can tell whether
    // the current pair is a matched one of the 4 options, or has drifted
    // (e.g. someone previously set them independently before this panel
    // started pairing them).
    const fetchCurrentModels = useCallback(async () => {
        try {
            const response = await fetch(API_URL, {
                method: "GET",
                headers: { "Content-Type": "application/json" },
            });

            const data = await response.json();

            if (response.ok && data.active_source && data.active_value_added_source) {
                const activeBase = data.active_source.toUpperCase();
                const activeVa = data.active_value_added_source.toUpperCase();
                setCurrentBaseModel(activeBase);
                setCurrentVaModel(activeVa);
                setSelectedModel(activeBase);
            } else {
                throw new Error(data.message || "Failed to fetch active sources");
            }
        } catch (error) {
            console.error("Error fetching current models:", error);
            setSnackbar({
                open: true,
                message: "Could not fetch active model sources. Please check your connection.",
                severity: "error",
            });
        } finally {
            setInitialLoading(false);
        }
    }, [API_URL]);

    useEffect(() => {
        fetchCurrentModels();
    }, [fetchCurrentModels]);

    const handleModelChange = (event) => {
        setSelectedModel(event.target.value);
    };

    const setSource = async (modelName) => {
        const response = await fetch(API_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ model_name: modelName }),
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
            throw new Error(data.message || data.error || `Failed to update ${modelName}`);
        }
    };

    const isPairActive = currentBaseModel === selectedModel && currentVaModel === vaNameFor(selectedModel);

    const handleSubmit = async () => {
        if (isPairActive || !selectedModel) return;
        setLoading(true);
        try {
            // Two independent POSTs on the backend, always kept in sync here.
            await setSource(selectedModel);
            await setSource(vaNameFor(selectedModel));
            await fetchCurrentModels();
            setSnackbar({
                open: true,
                message: `${selectedModel} is now the active forecast source (raw + value-added).`,
                severity: "success",
            });
        } catch (error) {
            console.error("Update Error:", error);
            setSnackbar({
                open: true,
                message: error.message || "Failed to update the forecast model source.",
                severity: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSnackbarClose = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    const currentModelInfo = MODEL_OPTIONS.find((m) => m.value === currentBaseModel) || MODEL_OPTIONS[1];

    const renderModelCard = (model) => (
        <Box
            key={model.value}
            sx={{
                flex: "1 1 320px",
                minWidth: 300,
                border: `2px solid ${selectedModel === model.value ? model.color : "#e2e8f0"}`,
                borderRadius: "12px",
                p: 3,
                transition: "all 0.2s ease",
                "&:hover": {
                    borderColor: model.color,
                    backgroundColor: `${model.color}05`,
                    transform: "translateY(-2px)",
                    boxShadow: `0 4px 12px ${model.color}20`
                },
                ...(selectedModel === model.value && {
                    backgroundColor: `${model.color}08`,
                    boxShadow: `0 4px 12px ${model.color}20`
                }),
            }}
        >
            <FormControlLabel
                value={model.value}
                control={<Radio sx={{ color: model.color, "&.Mui-checked": { color: model.color } }} />}
                label={
                    <Box sx={{ ml: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: "18px", color: "#1e293b" }}>
                                {model.label}
                            </Typography>
                            {model.value === currentBaseModel && currentVaModel === vaNameFor(model.value) && (
                                <Chip
                                    label="CURRENT ACTIVE"
                                    size="small"
                                    sx={{
                                        fontSize: "10px",
                                        fontWeight: 700,
                                        backgroundColor: "#16a34a",
                                        color: "white"
                                    }}
                                />
                            )}
                        </Box>
                        <Typography sx={{ fontSize: "13px", color: "#475569" }}>
                            {model.fullName}
                        </Typography>
                    </Box>
                }
                sx={{ width: "100%", margin: 0, alignItems: "flex-start" }}
            />
        </Box>
    );

    if (initialLoading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <div className="data-management-container">
            {/* Header */}
            <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: "16px", border: "1px solid #e2e8f0" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5, flexWrap: "wrap", gap: 1.5 }}>
                    <Typography sx={{ color: "#1e293b", fontWeight: 600, fontSize: "18px" }}>
                        Manage Forecast Model Source
                    </Typography>

                    <Chip
                        icon={<Refresh fontSize="small" />}
                        label={`Active Source: ${currentModelInfo.label}`}
                        sx={{
                            backgroundColor: `${currentModelInfo.color}18`,
                            color: currentModelInfo.color,
                            fontWeight: 700,
                            px: 2.5,
                            border: `1px solid ${currentModelInfo.color}30`,
                        }}
                    />
                </Box>

                <Typography sx={{ color: "#475569", fontSize: "14.8px", lineHeight: 1.7 }}>
                    Pick one of the 4 sources below. Updating sets it as the active source for both the raw
                    model output and its human-edited (value-added) counterpart together — e.g. selecting
                    ECMWF-IFS activates ECMWF-IFS for raw and ECMWF-IFS-VA for value added, in one step.
                </Typography>
            </Paper>

            {/* Source selection */}
            <Paper elevation={0} sx={{ p: 4, borderRadius: "16px", border: "1px solid #e2e8f0" }}>
                <Typography sx={{ fontWeight: 700, fontSize: "15px", color: "#64748b", textTransform: "uppercase", mb: 3 }}>
                    Select Forecast Model Source
                </Typography>

                <FormControl component="fieldset" fullWidth>
                    <RadioGroup
                        value={selectedModel}
                        onChange={handleModelChange}
                        sx={{ display: "flex", flexDirection: "row", gap: 2.5, flexWrap: "wrap" }}
                    >
                        {MODEL_OPTIONS.map((model) => renderModelCard(model))}
                    </RadioGroup>
                </FormControl>

                <Divider sx={{ my: 4 }} />

                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={() => setSelectedModel(currentBaseModel)}
                        disabled={loading}
                        sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            borderColor: "#cbd5e1",
                            color: "#475569",
                            "&:hover": {
                                borderColor: "#94a3b8",
                                backgroundColor: "#f8fafc"
                            }
                        }}
                    >
                        Reset
                    </Button>

                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={isPairActive || loading}
                        startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
                        sx={{
                            backgroundColor: "#2563eb",
                            textTransform: "none",
                            fontWeight: 700,
                            px: 5,
                            "&:hover": { backgroundColor: "#1d4ed8" },
                            "&:disabled": {
                                backgroundColor: "#e2e8f0",
                                color: "#94a3b8"
                            }
                        }}
                    >
                        {loading ? "Updating..." : "Update Source"}
                    </Button>
                </Box>
            </Paper>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ minWidth: "300px" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default DataManagement;
